import { toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api.js';
import type { AlprSample } from './types.js';

export const useAlprReview = () => {
  const qc = useQueryClient();
  const [index, setIndex] = useState(0);
  const [correction, setCorrection] = useState('');

  const queue = useQuery({
    queryKey: ['admin-alpr-pending'],
    queryFn: () =>
      api.request<{ samples: AlprSample[] }>(
        '/v1/admin/alpr/samples?status=pending_review&limit=20',
      ),
  });

  const samples = queue.data?.samples ?? [];
  const current = samples[index] ?? null;

  useEffect(() => {
    setCorrection(current?.predictedPlate ?? '');
  }, [current?.predictedPlate]);

  const review = useMutation({
    mutationFn: (body: { confirm?: boolean; reject?: boolean; actualPlate?: string }) => {
      if (!current) throw new Error('no sample');
      return api.request(`/v1/admin/alpr/samples/${current.id}/review`, {
        method: 'POST',
        json: body,
      });
    },
    onSuccess: () => {
      toast.success('Salvo.');
      void qc.invalidateQueries({ queryKey: ['admin-alpr-pending'] });
      setIndex((i) => Math.min(i, Math.max(0, samples.length - 2)));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (!current || review.isPending) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        review.mutate({ confirm: true });
      }
    },
    [current, review],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  const exportJsonl = () => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';
    window.open(`${base}/v1/admin/alpr/export?status=verified&plateFormat=mercosul`, '_blank');
  };

  return {
    queue,
    samples,
    current,
    index,
    setIndex,
    correction,
    setCorrection,
    review,
    exportJsonl,
  };
};
